/** Новая статья: карточка (раздел, владелец, роли, срок пересмотра) и первая версия — черновик */
import type { KbSection } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { type CardDraft, CardFields, type VersionDraft, VersionFields } from './ArticleForm';
import { metaQuery } from './shared';

export function NewArticlePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const meta = useQuery(metaQuery);
  useCrumbs([{ label: 'Новая статья' }]);
  const [card, setCard] = useState<CardDraft>({
    section: 'regulation',
    ownerId: '',
    ownerUnit: '',
    visibleRoles: [],
    reviewEveryMonths: '12',
  });
  const [ver, setVer] = useState<VersionDraft>({
    title: '',
    summary: '',
    body: '',
    changeNote: '',
    replacedParts: [],
  });
  const create = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/kb/articles', {
        method: 'POST',
        body: {
          section: card.section as KbSection,
          title: ver.title,
          summary: ver.summary || null,
          body: ver.body,
          ownerId: card.ownerId || null,
          ownerUnit: card.ownerUnit || null,
          visibleRoles: card.visibleRoles,
          reviewEveryMonths: Number(card.reviewEveryMonths) || 12,
        },
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['kb'] });
      toast('Черновик сохранён. Отправьте его на проверку владельцу.');
      navigate({ to: '/kb/$articleId', params: { articleId: r.id } });
    },
  });
  if (meta.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (meta.error)
    return (
      <Page>
        <ErrorBox error={meta.error} />
      </Page>
    );
  if (!meta.data.canEdit)
    return (
      <Page>
        <ErrorBox
          error={
            new Error(
              'Писать статьи может сотрудник с правом на документы. Обратитесь к администратору портала.',
            )
          }
        />
      </Page>
    );
  return (
    <Page>
      <PageHeader title="Новая статья" subtitle="Статья появится у читателей после проверки владельцем" />
      <div className="flex flex-col gap-4">
        <Panel title="Текст">
          <VersionFields value={ver} onChange={setVer} />
        </Panel>
        <Panel title="Владелец, видимость, пересмотр">
          <CardFields value={card} onChange={setCard} />
        </Panel>
        {create.error ? <ErrorBox error={create.error} /> : null}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={ver.title.trim().length < 3}
            onClick={() => create.mutate()}
          >
            Сохранить черновик
          </Button>
          <Button onClick={() => navigate({ to: '/kb' })}>Отмена</Button>
        </div>
      </div>
    </Page>
  );
}
